import { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { categoryLinks } from "@/mocks/categories";

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    if (mobileOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => { document.body.style.overflow = ""; };
  }, [mobileOpen]);

  // Close mobile menu on window resize to desktop
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 768) setMobileOpen(false);
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const handleMouseEnter = () => {
    if (closeTimer.current) {
      clearTimeout(closeTimer.current);
      closeTimer.current = null;
    }
    setDropdownOpen(true);
  };

  const handleMouseLeave = () => {
    closeTimer.current = setTimeout(() => {
      setDropdownOpen(false);
    }, 150);
  };

  const closeMobile = () => setMobileOpen(false);

  const isTransparent = !scrolled && !mobileOpen;
  const textClass = isTransparent
    ? "text-white hover:text-white/80"
    : "text-foreground-700 hover:text-primary-600";
  const logoTextClass = isTransparent
    ? "text-white"
    : "text-foreground-900";

  return (
    <>
      {/* ====== NAVBAR ====== */}
      <nav
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          scrolled || mobileOpen
            ? "bg-background-50/95 backdrop-blur-md border-b border-background-200/70"
            : "bg-transparent"
        }`}
      >
        <div className="w-full px-4 md:px-10">
          <div className="flex items-center justify-between h-16 md:h-18">
            {/* Logo */}
            <Link
              to="/"
              onClick={closeMobile}
              className={`flex items-center gap-2 transition-colors duration-200 ${logoTextClass}`}
            >
              <div className="w-8 h-8 md:w-9 md:h-9 flex items-center justify-center bg-primary-500 rounded-lg">
                <i className="ri-leaf-line text-lg text-white"></i>
              </div>
              <span className="text-lg md:text-xl font-heading tracking-tight">VigorBuy</span>
            </Link>

            {/* Desktop Center Nav */}
            <div className="hidden md:flex items-center gap-8">
              <Link
                to="/"
                className={`text-sm font-medium transition-colors duration-200 whitespace-nowrap ${textClass}`}
              >
                Home
              </Link>

              {/* Categories Dropdown */}
              <div
                className="relative"
                ref={dropdownRef}
                onMouseEnter={handleMouseEnter}
                onMouseLeave={handleMouseLeave}
              >
                <button
                  className={`flex items-center gap-1.5 text-sm font-medium transition-colors duration-200 whitespace-nowrap cursor-pointer ${textClass}`}
                >
                  Categories
                  <i
                    className={`ri-arrow-down-s-line text-base transition-transform duration-200 ${
                      dropdownOpen ? "rotate-180" : ""
                    }`}
                  ></i>
                </button>

                {dropdownOpen && (
                  <div className="absolute top-full left-1/2 -translate-x-1/2 mt-2 w-60 bg-background-50 rounded-xl border border-background-200/70 py-2 shadow-sm">
                    <div className="max-h-72 overflow-y-auto">
                      {categoryLinks.map((cat) => (
                        <a
                          key={cat.name}
                          href={cat.url}
                          target="_blank"
                          rel="noopener nofollow"
                          className="flex items-center gap-3 px-4 py-2.5 text-sm text-foreground-700 hover:bg-primary-50 hover:text-primary-700 transition-colors duration-150 cursor-pointer"
                        >
                          <div className="w-6 h-6 flex items-center justify-center">
                            <i className={`${cat.icon} text-base`}></i>
                          </div>
                          <span className="whitespace-nowrap">{cat.name}</span>
                        </a>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              <Link
                to="/article/vigorbuy-spreadsheet-guide"
                className={`text-sm font-medium transition-colors duration-200 whitespace-nowrap ${textClass}`}
              >
                Beginner Guide
              </Link>

              <Link
                to="/article/vigorbuy-spreadsheet-safety"
                className={`text-sm font-medium transition-colors duration-200 whitespace-nowrap ${textClass}`}
              >
                Safety
              </Link>

              <Link
                to="/article/vigorbuy-spreadsheet-shopping-list"
                className={`text-sm font-medium transition-colors duration-200 whitespace-nowrap ${textClass}`}
              >
                Shopping Guide
              </Link>
            </div>

            {/* Right CTAs */}
            <div className="flex items-center gap-3">
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className={`hidden sm:inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium transition-colors duration-200 whitespace-nowrap cursor-pointer ${
                  isTransparent ? "text-white/90 hover:text-white" : "text-foreground-700 hover:text-primary-600"
                }`}
              >
                vigorbuy sheet
                <span className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-arrow-right-up-line text-sm"></i>
                </span>
              </a>
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="inline-flex items-center gap-1.5 px-5 py-2 bg-primary-500 text-white text-sm font-medium rounded-full hover:bg-primary-600 transition-all duration-200 whitespace-nowrap cursor-pointer"
              >
                Buy It Now
                <span className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-arrow-right-up-line text-sm"></i>
                </span>
              </a>

              {/* Mobile Hamburger */}
              <button
                onClick={() => setMobileOpen((prev) => !prev)}
                className={`md:hidden flex items-center justify-center w-10 h-10 rounded-lg transition-colors duration-200 cursor-pointer ${
                  isTransparent ? "text-white hover:bg-white/10" : "text-foreground-700 hover:bg-background-100"
                }`}
                aria-label={mobileOpen ? "Close menu" : "Open menu"}
              >
                <span className="w-5 h-5 flex items-center justify-center">
                  <i className={`${mobileOpen ? "ri-close-line" : "ri-menu-line"} text-xl`}></i>
                </span>
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* ====== MOBILE MENU OVERLAY ====== */}
      {mobileOpen && (
        <div className="md:hidden fixed inset-0 z-40">
          {/* Dark backdrop */}
          <div
            className="absolute inset-0 bg-foreground-950/40"
            onClick={closeMobile}
          ></div>

          {/* Menu panel */}
          <div className="absolute top-16 left-0 right-0 bottom-0 bg-background-50 overflow-y-auto">
            <div className="px-4 py-6">
              {/* Quick Nav Links */}
              <div className="space-y-1 mb-6">
                <Link
                  to="/"
                  onClick={closeMobile}
                  className="flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium text-foreground-700 hover:bg-background-100 transition-colors duration-150"
                >
                  <span className="w-5 h-5 flex items-center justify-center">
                    <i className="ri-home-line text-base"></i>
                  </span>
                  Home
                </Link>
                <Link
                  to="/article/vigorbuy-spreadsheet-guide"
                  onClick={closeMobile}
                  className="flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium text-foreground-700 hover:bg-background-100 transition-colors duration-150"
                >
                  <span className="w-5 h-5 flex items-center justify-center">
                    <i className="ri-book-open-line text-base"></i>
                  </span>
                  Beginner Guide
                </Link>
                <Link
                  to="/article/vigorbuy-spreadsheet-safety"
                  onClick={closeMobile}
                  className="flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium text-foreground-700 hover:bg-background-100 transition-colors duration-150"
                >
                  <span className="w-5 h-5 flex items-center justify-center">
                    <i className="ri-shield-check-line text-base"></i>
                  </span>
                  Safety Guide
                </Link>
                <Link
                  to="/article/vigorbuy-spreadsheet-shopping-list"
                  onClick={closeMobile}
                  className="flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium text-foreground-700 hover:bg-background-100 transition-colors duration-150"
                >
                  <span className="w-5 h-5 flex items-center justify-center">
                    <i className="ri-shopping-bag-line text-base"></i>
                  </span>
                  Shopping Guide
                </Link>
              </div>

              {/* Categories Section */}
              <div className="border-t border-background-200/70 pt-5">
                <h3 className="text-xs font-semibold text-foreground-400 uppercase tracking-wider mb-3 px-4">
                  Categories
                </h3>
                <div className="grid grid-cols-2 gap-1">
                  {categoryLinks.map((cat) => (
                    <a
                      key={cat.name}
                      href={cat.url}
                      target="_blank"
                      rel="noopener nofollow"
                      onClick={closeMobile}
                      className="flex items-center gap-2.5 px-4 py-2.5 rounded-lg text-sm text-foreground-600 hover:bg-primary-50 hover:text-primary-700 transition-colors duration-150 cursor-pointer"
                    >
                      <span className="w-5 h-5 flex items-center justify-center">
                        <i className={`${cat.icon} text-sm`}></i>
                      </span>
                      <span className="whitespace-nowrap text-xs">{cat.name}</span>
                    </a>
                  ))}
                </div>
              </div>

              {/* Mobile CTAs */}
              <div className="border-t border-background-200/70 pt-5 mt-5 flex flex-col gap-3">
                <a
                  href="https://kaspreadsheet.com"
                  target="_blank"
                  rel="noopener nofollow"
                  onClick={closeMobile}
                  className="flex items-center justify-center gap-2 px-5 py-3 bg-primary-500 text-white text-sm font-medium rounded-full hover:bg-primary-600 transition-all duration-200 whitespace-nowrap cursor-pointer"
                >
                  Buy It Now
                  <span className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-arrow-right-up-line text-sm"></i>
                  </span>
                </a>
                <a
                  href="https://kaspreadsheet.com"
                  target="_blank"
                  rel="noopener nofollow"
                  onClick={closeMobile}
                  className="flex items-center justify-center gap-2 px-5 py-3 border-2 border-primary-500 text-primary-600 text-sm font-medium rounded-full hover:bg-primary-50 transition-all duration-200 whitespace-nowrap cursor-pointer"
                >
                  vigorbuy sheet
                  <span className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-arrow-right-up-line text-sm"></i>
                  </span>
                </a>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}